/*
  LIORIVE — Recently viewed products. Vanilla JS, no dependencies.
  Storage: localStorage key "liorive:recently-viewed" (array of product handles, newest first, max 12).
  Cards are fetched lazily (only when the section nears the viewport) through the Section Rendering API,
  only as many as can be shown: a sold-out or hidden piece is skipped and the next one fetched (it stays
  in storage, it may come back). Every step fails silently: with storage blocked, offline, or a deleted product, the section simply stays hidden.
*/
if (!customElements.get('recently-viewed-products')) {
  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      static KEY = 'liorive:recently-viewed';
      static MAX = 12;

      connectedCallback() {
        this.handle = this.dataset.handle || '';
        this.limit = parseInt(this.dataset.limit, 10) || 4;
        this.list = this.querySelector('[data-lr-list]');
        this.record();

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((e) => e.isIntersecting)) return;
              this.observer.disconnect();
              this.render();
            },
            { rootMargin: '300px' },
          );
          // The element itself starts hidden (display: none) and is never reported as intersecting:
          // observe its always-rendered section wrapper instead.
          this.observer.observe(this.closest(".shopify-section") || this.parentElement || this);
        } else {
          this.render();
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      read() {
        try {
          const value = JSON.parse(localStorage.getItem(RecentlyViewedProducts.KEY));
          return Array.isArray(value) ? value.filter((h) => typeof h === 'string' && h) : [];
        } catch (e) {
          return [];
        }
      }

      write(handles) {
        try {
          localStorage.setItem(RecentlyViewedProducts.KEY, JSON.stringify(handles.slice(0, RecentlyViewedProducts.MAX)));
        } catch (e) {
          /* storage unavailable: feature degrades to hidden */
        }
      }

      record() {
        if (!this.handle) return;
        this.write([this.handle, ...this.read().filter((h) => h !== this.handle)]);
      }

      async fetchCard(handle) {
        const url = `${this.dataset.root}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.dataset.sectionId)}`;
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) return { handle, gone: response.status === 404 };
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const template = doc.querySelector('template[data-lr-card]');
        // Shopify answers 200 with an empty section for a product that no longer exists.
        if (!template) return { handle, gone: true };
        const usable = template && template.dataset.available === 'true' && !template.hasAttribute('data-hidden');
        return { handle, html: usable ? template.innerHTML : '' };
      }

      async render() {
        if (!this.list) return;
        const exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
        const queue = [...new Set(this.read())].filter((h) => h !== this.handle && !exclude.includes(h));
        const cards = [];
        const gone = [];
        while (queue.length && cards.length < this.limit) {
          const batch = queue.splice(0, this.limit - cards.length);
          const results = await Promise.all(batch.map((h) => this.fetchCard(h).catch(() => ({ handle: h }))));
          results.forEach((r) => {
            if (r.gone) gone.push(r.handle);
            else if (r.html) cards.push(r);
          });
        }
        if (gone.length) this.write(this.read().filter((h) => !gone.includes(h)));
        if (!cards.length) return;
        this.list.innerHTML = cards.map((r) => r.html).join('');
        window.LioriveFavoris?.sync();
        this.hidden = false;
      }
    },
  );
}
